// Shared export utilities for CSV and PDF downloads

/**
 * Download data as CSV file
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (default: 'export.csv')
 * @param {Array} columns - Optional array of column keys to include (default: all keys)
 */
export function downloadCSV(data, filename = "export.csv", columns = null) {
  if (!data || !data.length) {
    console.warn("No data to export");
    return;
  }

  try {
    // Use provided columns or get all keys from first object
    const keys = columns || Object.keys(data[0]);

    // Create CSV rows
    const csvRows = [
      // Header row
      keys.map((key) => `"${key}"`).join(","),
      // Data rows
      ...data.map((row) =>
        keys
          .map((key) => {
            const value = row[key] ?? "";
            // Escape quotes and wrap in quotes
            return `"${String(value).replace(/"/g, '""')}"`;
          })
          .join(",")
      ),
    ];

    const csv = csvRows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);

    // Create download link
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.display = "none";

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Cleanup
    window.URL.revokeObjectURL(url);

    console.log(`CSV exported successfully: ${filename}`);
  } catch (error) {
    console.error("Error exporting CSV:", error);
    alert("Failed to export CSV file");
  }
}

/**
 * Download data as PDF file using jsPDF and jsPDF-AutoTable with multi-page support
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (default: 'export.pdf')
 * @param {Array} columns - Optional array of column keys to include (default: all keys)
 * @param {string} title - Optional title for the PDF
 * @param {Object} options - Additional options for PDF generation
 */
export function downloadPDF(
  data,
  filename = "export.pdf",
  columns = null,
  title = null,
  options = {}
) {
  if (!data || !data.length) {
    console.warn("No data to export");
    return;
  }

  // Default options optimized for landscape orientation
  const {
    maxColumnsPerPage = 10, // Increased for landscape - more columns can fit
    enableMultiPage = true, // Enable multi-page column layout
    fontSize = 9,
    headerFontSize = 10,
    cellPadding = 4,
    headerPadding = 5,
    minColumnWidth = 18, // Slightly reduced for landscape optimization
    maxColumnWidth = 35, // Adjusted for landscape layout
    ...otherOptions
  } = options;

  // Dynamically import jsPDF and jsPDF-AutoTable
  Promise.all([import("jspdf"), import("jspdf-autotable")])
    .then(([jsPDFModule, autoTableModule]) => {
      try {
        console.log("jsPDF module:", jsPDFModule);
        console.log("autoTable module:", autoTableModule);

        // Handle different import formats
        const jsPDF = jsPDFModule.default || jsPDFModule;
        console.log("jsPDF constructor:", jsPDF);

        if (!jsPDF) {
          throw new Error("jsPDF library not loaded properly");
        }

        // Handle autoTable import
        const autoTable = autoTableModule.default || autoTableModule;
        if (!autoTable) {
          throw new Error("jsPDF-AutoTable library not loaded properly");
        }

        // Use provided columns or get all keys from first object
        const keys = columns || Object.keys(data[0]);

        // Create PDF document - always use landscape for better multi-page layout
        // A4 landscape dimensions: 297mm x 210mm
        let doc;
        try {
          // Try newer API first
          doc = new jsPDF({
            orientation: "landscape",
            unit: "mm",
            format: "a4",
          });
        } catch (e) {
          console.log("New API failed, trying legacy API:", e);
          // Fallback to legacy API
          doc = new jsPDF("landscape", "mm", "a4");
        }

        // Force landscape orientation and set page size explicitly
        try {
          doc.internal.pageSize.setSize("a4", "landscape");
        } catch (e) {
          console.log("Could not set page size explicitly:", e);
        }

        // Ensure we're using landscape dimensions
        const pageWidth = 297; // A4 landscape width
        const pageHeight = 210; // A4 landscape height

        // Ensure landscape orientation is properly set
        console.log("PDF orientation:", doc.internal.pageSize.orientation);
        console.log(
          "Page dimensions:",
          doc.internal.pageSize.width,
          "x",
          doc.internal.pageSize.height
        );

        // Verify we have landscape dimensions (width > height)
        if (doc.internal.pageSize.width < doc.internal.pageSize.height) {
          console.warn(
            "Document is not in landscape mode! Attempting to fix..."
          );
          // Try to force landscape
          try {
            doc.internal.pageSize.setSize("a4", "landscape");
          } catch (e) {
            console.error("Could not force landscape mode:", e);
          }
        }

        // Test landscape orientation by adding a test line
        doc.setFontSize(12);
        doc.text("Landscape Test - This should be wide", 10, 10);
        console.log("Landscape test line added");

        // Add title if provided
        if (title) {
          doc.setFontSize(16);
          doc.setFont(undefined, "bold");
          doc.text(title, 14, 20);
          doc.setFontSize(fontSize);
          doc.setFont(undefined, "normal");
        }

        // Prepare table data with better header formatting
        const headers = keys.map((key) => {
          // Convert snake_case to Title Case
          let header =
            key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");

          // Handle common abbreviations and special cases
          const headerMappings = {
            created_at: "Created Date",
            created_time: "Created Time",
            book_in_date: "Book In Date",
            assignedMember: "Assigned Member",
            to_delete: "Actions",
            postcode: "Post Code",
            first_name: "First Name",
            last_name: "Last Name",
            mobile_number: "Mobile Number",
            date_of_birth: "Date of Birth",
            national_insurance_number: "National Insurance Number",
            criminal_record: "Criminal Record",
            medical_condition: "Medical Condition",
            security_question: "Security Question",
            security_answer: "Security Answer",
            emails_receive_marketing: "Marketing Emails",
            emails_updates_by: "Update Emails",
            emails_share_data_other_organisations: "Data Sharing",
            property_locations: "Property Locations",
            unique_guardian_number: "Guardian Number",
            hear_about_id: "How You Heard",
            gender_freetext: "Gender (Custom)",
            next_of_kin: "Next of Kin",
            criminal_record_description: "Criminal Record Details",
            medical_condition_description: "Medical Condition Details",
            updated_at: "Updated Date",
            verified: "Verified",
            role: "Role",
            department: "Department",
            position: "Position",
            status: "Status",
            urgency: "Urgency",
            deadline: "Deadline",
            completed: "Completed",
            type: "Type",
            property: "Property",
            name: "Name",
            email: "Email",
            id: "ID",
            property_id: "Property ID",
            client_id: "Client ID",
            client_contact_id: "Client Contact ID",
            building_manager_id: "Building Manager ID",
            block_id: "Block ID",
            inspection_route_id: "Inspection Route ID",
            status_manual: "Status Manual",
            main_photo: "Main Photo",
            spaces: "Spaces",
            price_from: "Price From",
            price_to: "Price To",
            expenditures_amount: "Expenditures Amount",
            date_taken_on: "Date Taken On",
            date_taken_back: "Date Taken Back",
            date_live: "Date Live",
            date_notice: "Date Notice",
            date_feature_from: "Date Feature From",
            date_feature_to: "Date Feature To",
            short_description: "Short Description",
            desc_edit: "Description Edit",
            build_in_contact_id: "Build In Contact ID",
            build_in_manager_id: "Build In Manager ID",
            build_in_agent_id: "Build In Agent ID",
            build_in_inspector_id: "Build In Inspector ID",
            inspection_id: "Inspection ID",
            actual_market_rent: "Actual Market Rent",
            location_id: "Location ID",
            type_id: "Type ID",
            bathrooms: "Bathrooms",
            kitchens: "Kitchens",
            garden: "Garden",
            parking: "Parking",
            payment: "Payment",
            approved: "Approved",
            property_type: "Property Type",
          };

          return headerMappings[header] || header;
        });

        // Enhanced status mapping for better readability
        const statusMapping = {
          0: "No",
          1: "Yes",
          2: "Pending",
          3: "Suspended",
          4: "Terminated",
          5: "On Hold",
          999: "Manual",
          true: "Yes",
          false: "No",
          null: "",
          undefined: "",
        };

        // Process rows to handle long text and null values
        const rows = data.map((row) =>
          keys.map((key) => {
            const value = row[key] ?? "";

            // Handle different data types
            if (value === null || value === undefined) {
              return "";
            }

            if (typeof value === "boolean") {
              return value ? "Yes" : "No";
            }

            if (typeof value === "number") {
              // Check if this is a status/boolean field that should be mapped
              if (
                key === "status" ||
                key === "status_manual" ||
                key === "verified" ||
                key === "completed" ||
                key === "approved" ||
                key === "garden" ||
                key === "parking" ||
                key === "main_photo"
              ) {
                const statusValue = statusMapping[value.toString()];
                return statusValue || value.toString();
              }
              return value.toString();
            }

            const stringValue = String(value);

            // Check if this is a status string that should be mapped
            if (
              key === "status" ||
              key === "status_manual" ||
              key === "verified" ||
              key === "completed" ||
              key === "approved" ||
              key === "garden" ||
              key === "parking" ||
              key === "main_photo"
            ) {
              const statusValue = statusMapping[stringValue];
              if (statusValue) {
                return statusValue;
              }
            }

            // Format dates for better readability
            if (key.includes("date") || key.includes("Date")) {
              try {
                const date = new Date(stringValue);
                if (!isNaN(date.getTime())) {
                  return date.toLocaleDateString();
                }
              } catch (e) {
                // If date parsing fails, return original value
              }
            }

            // Truncate very long text but allow more characters for better readability
            if (stringValue.length > 25) {
              return stringValue.substring(0, 22) + "...";
            }

            return stringValue;
          })
        );

        // Multi-page column layout
        if (enableMultiPage && keys.length > maxColumnsPerPage) {
          // Split columns into groups for multiple pages
          const columnGroups = [];
          for (let i = 0; i < keys.length; i += maxColumnsPerPage) {
            columnGroups.push(keys.slice(i, i + maxColumnsPerPage));
          }

          // Generate a page for each column group
          columnGroups.forEach((columnGroup, pageIndex) => {
            if (pageIndex > 0) {
              doc.addPage();
            }

            // Get corresponding headers and data for this column group
            const groupHeaders = columnGroup.map((key) => {
              const keyIndex = keys.indexOf(key);
              return headers[keyIndex];
            });

            const groupRows = rows.map((row) =>
              columnGroup.map((key) => {
                const keyIndex = keys.indexOf(key);
                return row[keyIndex];
              })
            );

            // Calculate optimal column widths for this group
            const margin = 15; // Increased margin for better landscape layout
            const availableWidth = pageWidth - margin * 2;

            const columnWidths = columnGroup.map((key, index) => {
              const headerLength = groupHeaders[index].length;
              const maxContentLength = Math.max(
                headerLength,
                ...groupRows.map((row) => String(row[index]).length)
              );

              // Calculate width based on content
              const contentWidth = Math.max(
                minColumnWidth,
                Math.min(maxColumnWidth, maxContentLength * 2.5 + 8)
              );

              return contentWidth;
            });

            // Ensure columns fit on page
            const totalTableWidth = columnWidths.reduce(
              (sum, width) => sum + width,
              0
            );

            if (totalTableWidth > availableWidth) {
              const scaleFactor = availableWidth / totalTableWidth;
              columnWidths.forEach((width, index) => {
                columnWidths[index] = Math.max(
                  minColumnWidth,
                  width * scaleFactor
                );
              });
            }

            // Calculate left margin to center the table
            const finalTableWidth = columnWidths.reduce(
              (sum, width) => sum + width,
              0
            );
            const leftMargin = Math.max(
              margin,
              (pageWidth - finalTableWidth) / 2
            );

            // Add page title for multi-page layout
            const pageTitle = title
              ? `${title} (Page ${pageIndex + 1} of ${columnGroups.length})`
              : `Page ${pageIndex + 1} of ${columnGroups.length}`;

            doc.setFontSize(14);
            doc.setFont(undefined, "bold");
            doc.text(pageTitle, 14, 20);
            doc.setFontSize(fontSize);
            doc.setFont(undefined, "normal");

            // Generate table for this column group
            autoTable(doc, {
              head: [groupHeaders],
              body: groupRows,
              startY: 30,
              margin: { top: 5, right: margin, bottom: 15, left: leftMargin },
              pageBreak: "auto",
              styles: {
                fontSize: fontSize,
                cellPadding: cellPadding,
                lineColor: [150, 150, 150],
                lineWidth: 0.2,
                textColor: [30, 30, 30],
                fontStyle: "normal",
                overflow: "linebreak",
                halign: "left",
                valign: "middle",
              },
              headStyles: {
                fillColor: [54, 123, 245],
                textColor: 255,
                fontStyle: "bold",
                fontSize: headerFontSize,
                cellPadding: headerPadding,
                halign: "center",
              },
              alternateRowStyles: {
                fillColor: [248, 249, 250],
              },
              columnStyles: {},
              didParseCell: function (data) {
                // Set specific column widths
                if (data.column.index < columnWidths.length) {
                  data.cell.styles.cellWidth = columnWidths[data.column.index];
                }
              },
              willDrawCell: function (data) {
                // Add borders
                data.cell.styles.lineWidth = 0.2;
              },
              didDrawPage: function (data) {
                // Add page number
                const pageCount = doc.internal.getNumberOfPages();
                doc.setFontSize(8);
                doc.text(
                  `Page ${data.pageNumber} of ${pageCount}`,
                  data.settings.margin.left,
                  doc.internal.pageSize.height - 8
                );
              },
              ...otherOptions,
            });
          });
        } else {
          // Single page layout for fewer columns
          const margin = 15; // Increased margin for better landscape layout
          const availableWidth = pageWidth - margin * 2;

          // Calculate optimal column widths
          const columnWidths = keys.map((key, index) => {
            const headerLength = headers[index].length;
            const maxContentLength = Math.max(
              headerLength,
              ...rows.map((row) => String(row[index]).length)
            );

            const contentWidth = Math.max(
              minColumnWidth,
              Math.min(maxColumnWidth, maxContentLength * 2.5 + 8)
            );

            return contentWidth;
          });

          // Ensure columns fit on page
          const totalTableWidth = columnWidths.reduce(
            (sum, width) => sum + width,
            0
          );

          if (totalTableWidth > availableWidth) {
            const scaleFactor = availableWidth / totalTableWidth;
            columnWidths.forEach((width, index) => {
              columnWidths[index] = Math.max(
                minColumnWidth,
                width * scaleFactor
              );
            });
          }

          // Calculate left margin to center the table
          const finalTableWidth = columnWidths.reduce(
            (sum, width) => sum + width,
            0
          );
          const leftMargin = Math.max(
            margin,
            (pageWidth - finalTableWidth) / 2
          );

          // Generate single page table
          autoTable(doc, {
            head: [headers],
            body: rows,
            startY: title ? 30 : 20,
            margin: { top: 5, right: margin, bottom: 15, left: leftMargin },
            pageBreak: "auto",
            styles: {
              fontSize: fontSize,
              cellPadding: cellPadding,
              lineColor: [150, 150, 150],
              lineWidth: 0.2,
              textColor: [30, 30, 30],
              fontStyle: "normal",
              overflow: "linebreak",
              halign: "left",
              valign: "middle",
            },
            headStyles: {
              fillColor: [54, 123, 245],
              textColor: 255,
              fontStyle: "bold",
              fontSize: headerFontSize,
              cellPadding: headerPadding,
              halign: "center",
            },
            alternateRowStyles: {
              fillColor: [248, 249, 250],
            },
            columnStyles: {},
            didParseCell: function (data) {
              // Set specific column widths
              if (data.column.index < columnWidths.length) {
                data.cell.styles.cellWidth = columnWidths[data.column.index];
              }
            },
            willDrawCell: function (data) {
              // Add borders
              data.cell.styles.lineWidth = 0.2;
            },
            didDrawPage: function (data) {
              // Add page number
              const pageCount = doc.internal.getNumberOfPages();
              doc.setFontSize(8);
              doc.text(
                `Page ${data.pageNumber} of ${pageCount}`,
                data.settings.margin.left,
                doc.internal.pageSize.height - 8
              );
            },
            ...otherOptions,
          });
        }

        // Save the PDF
        doc.save(filename);
        console.log(`PDF exported successfully: ${filename}`);
        console.log(
          "Final page dimensions:",
          doc.internal.pageSize.width,
          "x",
          doc.internal.pageSize.height
        );
        console.log("Final orientation:", doc.internal.pageSize.orientation);
      } catch (error) {
        console.error("Error exporting PDF:", error);
        console.error("Error details:", error.message, error.stack);
        alert(`Failed to export PDF file: ${error.message}`);
      }
    })
    .catch((error) => {
      console.error("Error loading PDF libraries:", error);
      console.error("Error details:", error.message, error.stack);
      alert(
        `PDF export libraries not available: ${error.message}. Please ensure jspdf and jspdf-autotable are installed.`
      );
    });
}

/**
 * Download file from backend API endpoint
 * @param {string} endpoint - API endpoint to call
 * @param {string} filename - Name for the downloaded file
 * @param {Object} options - Additional options (method, headers, body)
 */
export function downloadFromAPI(endpoint, filename, options = {}) {
  const { method = "GET", headers = {}, body = null } = options;

  // Add authorization header if token exists
  const token = localStorage.getItem("token");
  const requestHeaders = {
    "Content-Type": "application/json",
    ...headers,
  };

  if (token) {
    requestHeaders.Authorization = `Bearer ${token}`;
  }

  fetch(endpoint, {
    method,
    headers: requestHeaders,
    body: body ? JSON.stringify(body) : null,
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.blob();
    })
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      console.log(`File downloaded successfully: ${filename}`);
    })
    .catch((error) => {
      console.error("Error downloading file:", error);
      alert("Failed to download file from server");
    });
}

/**
 * Export data with confirmation dialog
 * @param {Function} exportFunction - Function to call for export
 * @param {string} format - Format type ('CSV' or 'PDF')
 * @param {Array} data - Data to export
 * @param {string} filename - Filename for export
 * @param {Array} columns - Optional columns to include
 * @param {string} title - Optional title for PDF
 * @param {Object} options - Optional PDF export options
 */
export function exportWithConfirmation(
  exportFunction,
  format,
  data,
  filename,
  columns = null,
  title = null,
  options = {}
) {
  if (!data || data.length === 0) {
    alert(`No data available to export as ${format}`);
    return;
  }

  const confirmed = window.confirm(
    `Export ${data.length} records as ${format}?\n\nFilename: ${filename}`
  );

  if (confirmed) {
    if (format === "PDF") {
      exportFunction(data, filename, columns, title, options);
    } else {
      exportFunction(data, filename, columns);
    }
  }
}

/**
 * Export data as multi-page PDF with optimized column widths for landscape orientation
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (default: 'export.pdf')
 * @param {Array} columns - Optional array of column keys to include (default: all keys)
 * @param {string} title - Optional title for the PDF
 * @param {Object} options - Additional options for PDF generation
 */
export function downloadMultiPagePDF(
  data,
  filename = "export.pdf",
  columns = null,
  title = null,
  options = {}
) {
  const defaultOptions = {
    maxColumnsPerPage: 10, // Optimized for landscape - more columns per page
    enableMultiPage: true,
    fontSize: 9,
    headerFontSize: 10,
    cellPadding: 4,
    headerPadding: 5,
    minColumnWidth: 18, // Optimized for landscape layout
    maxColumnWidth: 35, // Optimized for landscape layout
    ...options,
  };

  return downloadPDF(data, filename, columns, title, defaultOptions);
}

/**
 * Export data as single-page PDF with wide columns optimized for landscape
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (default: 'export.pdf')
 * @param {Array} columns - Optional array of column keys to include (default: all keys)
 * @param {string} title - Optional title for the PDF
 * @param {Object} options - Additional options for PDF generation
 */
export function downloadWideColumnPDF(
  data,
  filename = "export.pdf",
  columns = null,
  title = null,
  options = {}
) {
  const defaultOptions = {
    maxColumnsPerPage: 30, // Increased for landscape - more columns can fit
    enableMultiPage: false,
    fontSize: 8,
    headerFontSize: 9,
    cellPadding: 3,
    headerPadding: 4,
    minColumnWidth: 12, // Optimized for landscape
    maxColumnWidth: 28, // Optimized for landscape
    ...options,
  };

  return downloadPDF(data, filename, columns, title, defaultOptions);
}

/**
 * Download data as Excel file using SheetJS (xlsx)
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (default: 'export.xlsx')
 * @param {Array} columns - Optional array of column keys to include (default: all keys)
 * @param {string} sheetName - Optional sheet name (default: 'Sheet1')
 */
export function downloadExcel(
  data,
  filename = "export.xlsx",
  columns = null,
  sheetName = "Sheet1"
) {
  if (!data || !data.length) {
    console.warn("No data to export");
    return;
  }

  // Dynamically import xlsx
  import("xlsx")
    .then((XLSX) => {
      try {
        // Use provided columns or get all keys from first object
        const keys = columns || Object.keys(data[0]);

        // Create headers with proper formatting
        const headers = keys.map((key) => {
          // Convert snake_case to Title Case
          return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
        });

        // Process data rows
        const rows = data.map((row) =>
          keys.map((key) => {
            const value = row[key] ?? "";

            // Handle different data types
            if (value === null || value === undefined) {
              return "";
            }

            if (typeof value === "boolean") {
              return value ? "Yes" : "No";
            }

            return value;
          })
        );

        // Create worksheet with headers and data
        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

        // Auto-size columns based on content
        const columnWidths = keys.map((key, index) => {
          const headerLength = headers[index].length;
          const maxContentLength = Math.max(
            headerLength,
            ...rows.map((row) => String(row[index]).length)
          );
          return { wch: Math.min(50, Math.max(10, maxContentLength + 2)) };
        });
        ws["!cols"] = columnWidths;

        // Create workbook and add worksheet
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);

        // Write file
        XLSX.writeFile(wb, filename);

        console.log(`Excel file exported successfully: ${filename}`);
      } catch (error) {
        console.error("Error exporting Excel:", error);
        alert(`Failed to export Excel file: ${error.message}`);
      }
    })
    .catch((error) => {
      console.error("Error loading Excel library:", error);
      alert(
        "Excel export library not available. Please ensure xlsx is installed."
      );
    });
}

/**
 * Print table data (opens print dialog)
 * @param {Array} data - Array of objects to print
 * @param {Array} columns - Optional array of column keys to include
 * @param {string} title - Optional title for the print page
 */
export function printTable(data, columns = null, title = null) {
  if (!data || !data.length) {
    console.warn("No data to print");
    return;
  }

  try {
    const keys = columns || Object.keys(data[0]);

    // Create headers
    const headers = keys.map((key) => {
      return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
    });

    // Create HTML table
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title || "Print Table"}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
          }
          h1 {
            font-size: 18px;
            margin-bottom: 20px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
          }
          th {
            background-color: #367BF5;
            color: white;
            padding: 10px;
            text-align: left;
            font-weight: bold;
            border: 1px solid #ddd;
          }
          td {
            padding: 8px;
            border: 1px solid #ddd;
          }
          tr:nth-child(even) {
            background-color: #f8f9fa;
          }
          @media print {
            body { margin: 0; }
            button { display: none; }
          }
        </style>
      </head>
      <body>
        ${title ? `<h1>${title}</h1>` : ""}
        <table>
          <thead>
            <tr>
              ${headers.map((h) => `<th>${h}</th>`).join("")}
            </tr>
          </thead>
          <tbody>
            ${data
              .map(
                (row) =>
                  `<tr>${keys
                    .map((key) => {
                      const value = row[key] ?? "";
                      return `<td>${value}</td>`;
                    })
                    .join("")}</tr>`
              )
              .join("")}
          </tbody>
        </table>
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
      </html>
    `;

    // Open print dialog in new window
    const printWindow = window.open("", "_blank");
    printWindow.document.write(html);
    printWindow.document.close();
  } catch (error) {
    console.error("Error printing table:", error);
    alert("Failed to print table");
  }
}
